import { zodResolver } from "@hookform/resolvers/zod";
import { X } from "lucide-react";
import type { ComponentProps, ReactNode, Ref } from "react";
import { useImperativeHandle, useRef, useState } from "react";
import type { DeepPartial, UseFormReturn } from "react-hook-form";
import { useForm, useWatch } from "react-hook-form";
import { z } from "zod";

import { Create } from "../model";

import { OrderEntity } from "~/entities/order";
import type { ServiceEntity } from "~/entities/service";
import { groupSelectedServices } from "~/entities/service";
import { ServiceSelectionDialog } from "~/features/service/@x/order";
import { sum } from "~/shared/arr";
import { formatCurrency, Tk, useTranslation2 } from "~/shared/i18n";
import { iife } from "~/shared/no-topic";
import { useAutofocusFormField } from "~/shared/react-hook-form";
import { COLON_WITH_SPACE, EMPTY, QUESTION_MARK } from "~/shared/str";
import { cn } from "~/shared/ui";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "~/shared/ui/accordion";
import { Button } from "~/shared/ui/button";
import { disablePastDates } from "~/shared/ui/calendar";
import { Checkbox } from "~/shared/ui/checkbox";
import { DateTimePicker } from "~/shared/ui/date-time-picker";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  getFieldSchema,
  INFER_ATTRS,
} from "~/shared/ui/form";
import { Input } from "~/shared/ui/input";
import { PhoneInput } from "~/shared/ui/phone-input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "~/shared/ui/select";
import { Textarea } from "~/shared/ui/textarea";
import type { Quantity } from "~/shared/zod";
import { DEFAULT_ZOD_PARSE_PARAMS, errorMapForForms2 } from "~/shared/zod";

type FieldMetadata<T extends string> = {
  name: T;
  labelTk: Tk;
};

const zFormControlComponentType = z.enum(["INPUT", "SELECT", "CHECKBOX"]);
const FormControlComponentType = zFormControlComponentType.Values;
type FormControlComponentType = z.infer<typeof zFormControlComponentType>;

const FROM_TO_FIELD_METADATA = {
  INPUT: [
    {
      name: "city",
      labelTk: Tk.CITY,
    },
    {
      name: "address",
      labelTk: Tk.ADDRESS,
    },
    {
      name: "floor",
      labelTk: Tk.FLOOR,
    },
    {
      name: "postalCode",
      labelTk: Tk.POSTAL_CODE,
    },
    {
      name: "floorArea",
      labelTk: Tk.AREA,
    },
    {
      name: "numberOfRooms",
      labelTk: Tk.NUMBER_OF_ROOMS,
    },
  ],
  SELECT: [
    {
      name: "buildingType",
      labelTk: Tk.BUILDING_TYPE,
    },
  ],
  CHECKBOX: [
    {
      name: "hasElevator",
      labelTk: Tk.IS_THERE_AN_ELEVATOR,
    },
    {
      name: "hasAttic",
      labelTk: Tk.IS_THERE_AN_ATTIC,
    },
    {
      name: "hasBasement",
      labelTk: Tk.IS_THERE_A_BASEMENT,
    },
  ],
} satisfies Record<FormControlComponentType, FieldMetadata<keyof Create.Schema["from"]>[]>;

const CONTACT_PERSON_FIELD_METADATA = {
  INPUT: [
    {
      name: "fullName",
      labelTk: Tk.FULL_NAME,
    },
    {
      name: "email",
      labelTk: Tk.EMAIL,
    },
    {
      name: "phone",
      labelTk: Tk.PHONE,
    },
  ],
  SELECT: [
    {
      name: "gender",
      labelTk: Tk.GENDER,
    },
  ],
} satisfies Record<
  Exclude<FormControlComponentType, "CHECKBOX">,
  FieldMetadata<keyof Create.Schema["contactPerson"]>[]
>;

type Handle = {
  divElement: HTMLDivElement;
  formElement: HTMLFormElement;
  hookForm: UseFormReturn<Create.Schema>;
} | null;

type Props = Omit<ComponentProps<"div">, "ref"> & {
  ref?: Ref<Handle> | undefined;
  services?: ServiceEntity.Schema[] | undefined;
  selectedServices?:
    | (Pick<ServiceEntity.Schema, "id"> & {
        count: number;
      })[]
    | undefined;
  defaultValues?:
    | DeepPartial<
        Omit<Create.Schema, "contactPerson"> & {
          contactPerson: Omit<Create.Schema["contactPerson"], "email"> & {
            email: Create.Schema["contactPerson"]["email"] | null;
          };
        }
      >
    | undefined;
  moveIsScheduledForDisabled?: ComponentProps<typeof DateTimePicker>["disabled"] | undefined;
  showServicesControl?: boolean | undefined;
  formProps?:
    | (Omit<ComponentProps<"form">, "onSubmit"> & {
        onSubmit?: Parameters<UseFormReturn<Create.Schema>["handleSubmit"]>[0] | undefined;
      })
    | undefined;
} & Pick<Parameters<typeof Create.zSchema>[0], "checkMoveIsScheduledFor">;

function CreationForm({
  ref,
  services = [],
  selectedServices = [],
  className,
  defaultValues,
  moveIsScheduledForDisabled = disablePastDates,
  showServicesControl = true,
  formProps: { id: formId, className: formClassName, onSubmit, ...formProps } = {},
  checkMoveIsScheduledFor,
  ...props
}: Props) {
  const {
    translation: { t },
    postproc: { tc },
  } = useTranslation2();

  const [groupedSelectedServices, setGroupedSelectedServices] = useState(() =>
    groupSelectedServices(services, selectedServices),
  );

  const form = useForm({
    resolver: zodResolver(
      Create.zSchema({
        checkMoveIsScheduledFor,
      }),
      {
        ...DEFAULT_ZOD_PARSE_PARAMS,
        errorMap: errorMapForForms2(t),
      },
    ),
    defaultValues: {
      id: defaultValues?.id ?? crypto.randomUUID(),
      ...Object.fromEntries(
        (["from", "to"] as const).map((key) => [
          key,
          {
            city: defaultValues?.[key]?.city ?? EMPTY,
            address: defaultValues?.[key]?.address ?? EMPTY,
            floor: defaultValues?.[key]?.floor ?? (EMPTY as unknown as number),
            postalCode: defaultValues?.[key]?.postalCode ?? EMPTY,
            buildingType:
              defaultValues?.[key]?.buildingType ??
              Create.zBaseSchema.shape[key]?.shape.buildingType.Values.PRIVATE_HOUSE,
            floorArea: defaultValues?.[key]?.floorArea ?? (EMPTY as unknown as number),
            numberOfRooms: defaultValues?.[key]?.numberOfRooms ?? (EMPTY as unknown as number),
            hasElevator: defaultValues?.[key]?.hasElevator ?? false,
            hasAttic: defaultValues?.[key]?.hasAttic ?? false,
            hasBasement: defaultValues?.[key]?.hasBasement ?? false,
          } satisfies NonNullable<
            Extract<
              NonNullable<Parameters<typeof useForm<Create.Schema>>[0]>["defaultValues"],
              Record<string, unknown>
            >[typeof key]
          >,
        ]),
      ),
      cargoDescription: defaultValues?.cargoDescription ?? EMPTY,
      referralInfo: defaultValues?.referralInfo ?? {
        source: Create.zBaseSchema.shape.referralInfo.options[0].shape.source.Values.WEB_SEARCH,
      },
      contactPerson: iife(() => {
        const { email } = defaultValues?.contactPerson ?? {};
        return {
          fullName: EMPTY,
          phone: EMPTY,
          gender: Create.zBaseSchema.shape.contactPerson.shape.gender.Values.MALE,
          ...defaultValues?.contactPerson,
          email: email ?? EMPTY,
        };
      }),
      moveIsScheduledFor:
        defaultValues?.moveIsScheduledFor ??
        iife(() => {
          const value = new Date();
          value.setHours(value.getHours() + 1);
          return value;
        }),
      services:
        defaultValues?.services ??
        Object.values(groupedSelectedServices)
          .filter((value) => !!value)
          .flat()
          .map(({ id, count }) => ({
            id,
            quantity: count,
          })),
    },
  });

  const referralInfoSource = useWatch({
    control: form.control,
    name: "referralInfo.source",
  });

  useAutofocusFormField(form, "contactPerson.fullName");

  const divRef = useRef<HTMLDivElement>(null);
  const formRef = useRef<HTMLFormElement>(null);

  useImperativeHandle<Handle, Handle>(ref, () => {
    const formElement = formRef.current;
    const divElement = divRef.current;
    if (!(divElement && formElement)) return null;

    return {
      divElement,
      formElement,
      hookForm: form,
    };
  }, [form]);

  const [isServiceSelectionDialogOpen, setServiceSelectionDialogOpen] = useState(false);

  const updateOrderedServices: ComponentProps<typeof ServiceSelectionDialog>["onSubmit"] = (
    data,
  ) => {
    form.setValue(
      "services",
      Object.entries(data)
        .map(([, services]) => services)
        .flat()
        .filter(({ count }) => count > 0)
        .map(({ count, ...rest }) => ({
          ...rest,
          quantity: count as Quantity,
        })),
    );
    setGroupedSelectedServices(
      groupSelectedServices(
        services,
        form.getValues().services.map(({ quantity, ...rest }) => ({
          ...rest,
          count: quantity,
        })),
      ),
    );
    setServiceSelectionDialogOpen(false);
  };

  const handleSubmit: Parameters<(typeof form)["handleSubmit"]>[0] = (data) => {
    onSubmit?.(data);
  };

  return (
    <Form {...form}>
      <div ref={divRef} className={cn("flex flex-col gap-4", className)} {...props}>
        {showServicesControl && (
          <FormFieldset legendText={tc(Tk.SELECTED_SERVICES)}>
            {Object.keys(groupedSelectedServices).length > 0 ? (
              <Accordion type="multiple">
                {Object.entries(groupedSelectedServices).map(([categoryId, services]) => {
                  if (!services) throw new Error("Services must be present");

                  const categoryName = services.find(
                    (service) => service.category.id === categoryId,
                  )?.category.name;

                  return (
                    <AccordionItem key={categoryId} value={categoryId}>
                      <AccordionTrigger>
                        <span className="mr-auto">{categoryName}</span>
                        <span className="order-1">
                          {formatCurrency(sum(services, ({ price, count }) => price * count))}
                        </span>
                      </AccordionTrigger>
                      <AccordionContent>
                        <ul>
                          {services.map(({ id, name, price, count }) => {
                            return (
                              <li
                                key={id}
                                className="grid grid-cols-[4fr_repeat(3,_1fr)] items-center gap-x-2 [&>span:not(:first-child)]:text-end"
                              >
                                <span className="max-xs:col-span-4">{name}</span>
                                <span className="max-xs:col-span-2">{formatCurrency(price)}</span>
                                <span className="flex items-center justify-end">
                                  <X className="size-4" />
                                  {count}
                                </span>
                                <span>{formatCurrency(price * count)}</span>
                              </li>
                            );
                          })}
                        </ul>
                      </AccordionContent>
                    </AccordionItem>
                  );
                })}
              </Accordion>
            ) : (
              <p className="text-center">{tc(Tk.YOU_HAVE_NOT_SELECTED_ANY_SERVICES)}</p>
            )}
            <div className="flex items-center justify-between">
              <ServiceSelectionDialog
                services={services}
                orderedServices={form.getValues("services").map((orderedService) => {
                  const { name = EMPTY, price = 0 } =
                    services.find(({ id }) => id === orderedService.id) ?? {};
                  return {
                    ...orderedService,
                    name,
                    price,
                    quantity: orderedService.quantity as Quantity,
                  };
                })}
                onSubmit={updateOrderedServices}
                open={isServiceSelectionDialogOpen}
                onOpenChange={setServiceSelectionDialogOpen}
                triggerProps={{
                  children: <Button>{tc(Tk.EDIT)}</Button>,
                }}
              />
              <span className="text-end">
                {[
                  tc(Tk.TOTAL_COST),
                  formatCurrency(
                    sum(
                      Object.values(groupedSelectedServices).flat(),
                      (service) => (service?.price ?? 0) * (service?.count ?? 0),
                    ),
                  ),
                ].join(COLON_WITH_SPACE)}
              </span>
            </div>
          </FormFieldset>
        )}
        <form
          ref={formRef}
          id={formId}
          className={cn("flex flex-col gap-4", formClassName)}
          onSubmit={(event) => void form.handleSubmit(handleSubmit)(event)}
          {...formProps}
        >
          <FormFieldset legendText={tc(Tk.CONTACT_PERSON)}>
            {CONTACT_PERSON_FIELD_METADATA.INPUT.map(({ name, labelTk }) => (
              <FormField
                key={name}
                control={form.control}
                name={`contactPerson.${name}`}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      {labelTk === "PHONE" ? tc(labelTk, { count: 1 }) : tc(labelTk)}
                    </FormLabel>
                    <FormControl>
                      {name === "phone" ? <PhoneInput {...field} /> : <Input {...field} />}
                    </FormControl>
                    {name === "phone" && (
                      <FormDescription>
                        {tc(Tk["ENTER_THE_VALUE_IN_INTERNATIONAL_PHONE_NUMBER_FORMAT_I_E_..."])}
                      </FormDescription>
                    )}
                    <FormMessage />
                  </FormItem>
                )}
              />
            ))}
            {CONTACT_PERSON_FIELD_METADATA.SELECT.map(({ name, labelTk }) => (
              <FormField
                key={name}
                control={form.control}
                name={`contactPerson.${name}`}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{tc(labelTk)}</FormLabel>
                    <Select value={field.value} onValueChange={field.onChange}>
                      <FormControl>
                        <SelectTrigger className="w-full">
                          <SelectValue />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {Create.zBaseSchema.shape.contactPerson.shape.gender.options.map(
                          (option) => (
                            <SelectItem key={option} value={option}>
                              {tc(Tk[option])}
                            </SelectItem>
                          ),
                        )}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            ))}
          </FormFieldset>
          <div className="flex flex-col gap-4 md:flex-row md:*:w-1/2">
            <FromToFormFields formControl={form.control} fromOrTo="from" />
            <FromToFormFields formControl={form.control} fromOrTo="to" />
          </div>
          <div className="flex flex-col gap-4 rounded-md border p-6">
            <FormField
              control={form.control}
              name="cargoDescription"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{tc(Tk.DESCRIPTION_OF_CARGO)}</FormLabel>
                  <FormControl>
                    <Textarea {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="moveIsScheduledFor"
              render={({ field }) => (
                <FormItem className="flex flex-col">
                  <FormLabel>{tc(Tk.PLANNED_MOVING_DATE)}</FormLabel>
                  <DateTimePicker
                    triggerProps={{
                      children: (button) => <FormControl className="w-full">{button}</FormControl>,
                    }}
                    disabled={moveIsScheduledForDisabled}
                    {...field}
                  />
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="referralInfo.source"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{tc(Tk.HOW_DID_YOU_HEAR_ABOUT_US)}</FormLabel>
                  <Select value={field.value} onValueChange={field.onChange}>
                    <FormControl>
                      <SelectTrigger className="w-full">
                        <SelectValue />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {OrderEntity.zReferralSourceSchema.options.map((option) => (
                        <SelectItem key={option} value={option}>
                          {tc(
                            Tk[
                              {
                                [OrderEntity.zReferralSourceSchema.Values.WEB_SEARCH]:
                                  Tk.WEB_SEARCH,
                                [OrderEntity.zReferralSourceSchema.Values.FRIENDS_RECOMMENDATIONS]:
                                  Tk.FRIEND_RECOMMENDATIONS,
                                [OrderEntity.zReferralSourceSchema.Values.SOCIAL_NETWORKS]:
                                  Tk.SOCIAL_MEDIA,
                                [OrderEntity.zReferralSourceSchema.Values.ADVERTISEMENT]:
                                  Tk.ADVERTISING,
                                [OrderEntity.zReferralSourceSchema.Values.OTHER]: Tk.OTHER,
                              }[option]
                            ],
                          )}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
            {referralInfoSource === OrderEntity.zReferralSourceSchema.Values.OTHER && (
              <FormField
                control={form.control}
                name="referralInfo.details"
                render={({ field: { value = EMPTY, ...field } }) => (
                  <FormItem>
                    <FormLabel>{tc(Tk.PLEASE_TELL_US_HOW_YOU_FOUND_OUT_ABOUT_US)}</FormLabel>
                    <FormControl>
                      <Input value={value} {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}
          </div>
        </form>
      </div>
    </Form>
  );
}

type FromToFormFieldsProps = Omit<
  ComponentProps<typeof FormFieldset>,
  "legendText" | "children"
> & {
  formControl: UseFormReturn<Create.Schema>["control"];
  fromOrTo: Extract<keyof Create.Schema, "from" | "to">;
};

function FromToFormFields({ formControl, fromOrTo, className, ...props }: FromToFormFieldsProps) {
  const {
    translation: { t },
    postproc: { tc, ts },
  } = useTranslation2();

  return (
    <FormFieldset legendText={tc(fromOrTo === "from" ? Tk.FROM_WHERE : Tk.TO)} {...props}>
      {FROM_TO_FIELD_METADATA.INPUT.map(({ name, labelTk }) => {
        const fieldName = `${fromOrTo}.${name}` as const;
        return (
          <FormField
            key={name}
            control={formControl}
            name={fieldName}
            render={(context) => {
              const { field } = context;
              const fieldSchema = getFieldSchema(Create.zBaseSchema, fieldName);

              return (
                <FormItem>
                  <FormLabel>
                    {labelTk === Tk.ADDRESS
                      ? tc(labelTk, {
                          count: 1,
                        })
                      : tc(labelTk)}
                    {name === "floorArea" ? (
                      <span>
                        ({t(Tk.M)}
                        <sup>2</sup>)
                      </span>
                    ) : (
                      EMPTY
                    )}
                  </FormLabel>
                  <FormControl>
                    <Input {...field} {...INFER_ATTRS.input(fieldSchema, context)} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              );
            }}
          />
        );
      })}
      {FROM_TO_FIELD_METADATA.SELECT.map(({ name, labelTk }) => (
        <FormField
          key={name}
          control={formControl}
          name={`${fromOrTo}.${name}`}
          render={({ field }) => (
            <FormItem>
              <FormLabel>{tc(labelTk)}</FormLabel>
              <Select value={field.value} onValueChange={field.onChange}>
                <FormControl>
                  <SelectTrigger className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {Create.zBaseSchema.shape[fromOrTo].shape[name].options.map((option) => (
                    <SelectItem key={option} value={option}>
                      {tc(Tk[option])}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />
      ))}
      {FROM_TO_FIELD_METADATA.CHECKBOX.map(({ name, labelTk }) => (
        <FormField
          key={name}
          control={formControl}
          name={`${fromOrTo}.${name}`}
          render={({ field }) => (
            <FormItem className="flex items-center">
              <FormControl>
                <Checkbox checked={field.value} onCheckedChange={field.onChange} />
              </FormControl>
              <FormLabel className="text-sm font-normal">{ts(QUESTION_MARK)(labelTk)}</FormLabel>
            </FormItem>
          )}
        />
      ))}
    </FormFieldset>
  );
}

type FormFieldsetProps = Omit<ComponentProps<"fieldset">, "children"> & {
  legendText: string;
  children: NonNullable<ReactNode>;
};

function FormFieldset({ legendText, children, className, ...props }: FormFieldsetProps) {
  return (
    <fieldset className={cn("flex flex-col gap-4 rounded-md border p-6", className)} {...props}>
      <legend>{legendText}</legend>
      {children}
    </fieldset>
  );
}

export { CreationForm };
export type { Handle };
